// The drawn icons of the INBOX (docs/design/inbox.md §6; round 089 cards
// 01 and 02): one icon for each bucket, and the marks of the figure
// cells that the resource icons of `ui/resicon.ts` do not draw. They are
// one family with the dock icons: a 24-unit box, a stroke of 1.7 and
// round joins, in `currentColor`, so the ink of a row is the ink of its
// icon. A bucket shows by its shape first and by its ink second (build
// plan §1 rule 2), so no two bucket icons share an outline.
//
// Every icon is hidden from a screen reader: the row states its kind in
// words (audit P13). `ui/lineicon.ts` writes the markup of the family.

import { lineIcon } from '../lineicon';

/** The paths of each icon. A filled part states its own fill. */
const PATHS = {
  // A hull on the water line and an arrow down: the bucket LOSS.
  loss: '<path d="M3.5 15h17l-2.8 4.5H6.3z"/><path d="M12 3.5v8M8.5 8.5L12 12l3.5-3.5"/>',
  // Two crossed blades: the bucket WAR.
  war: '<path d="M4.5 4.5l11 11M19.5 4.5l-11 11"/><path d="M13 17.8l4.8-4.8M6.2 13l4.8 4.8"/>'
    + '<path d="M16.7 16.7l2.8 2.8M7.3 16.7l-2.8 2.8"/>',
  // The hold with its flag: the bucket HOLD.
  hold: '<path d="M3 16.5l4.5-3h9l4.5 3-4.5 3h-9z"/><path d="M12 13.5V4.5"/><path d="M12 4.5l5.5 2.6L12 9.7"/>',
  // A sounding line and its lead under the surface: the bucket DIVE.
  dive: '<path d="M3 5.5c1.5-1.2 3-1.2 4.5 0s3 1.2 4.5 0 3-1.2 4.5 0 3 1.2 4.5 0"/><path d="M12 5.5v9.5"/>'
    + '<path d="M8.5 9h3.5M8.5 12.5h3.5"/><path d="M10.2 15h3.6l-.6 5.5h-2.4z"/>',
  // A price tag: the bucket TRADE.
  trade: '<path d="M3.5 12.2V4.5h7.7l9.3 9.3-7.7 7.7z"/><circle cx="7.8" cy="8.8" r="1.4"/>',
  // A buoy on the water line: the bucket WORLD.
  world: '<circle cx="12" cy="10" r="5.5"/><circle cx="12" cy="10" r="1.5" fill="currentColor" stroke="none"/>'
    + '<path d="M3.5 19.5c1.4-1 2.8-1 4.2 0s2.8 1 4.3 0 2.8-1 4.3 0 2.8 1 4.2 0"/>',
  // A burst: the damage mark of a figure cell.
  damage: '<path d="M12 3.5l1.9 5 5.1-1.6-2.9 4.5 4.4 2.9-5.3.5.6 5.2L12 16.6l-3.8 3.4.6-5.2-5.3-.5'
    + ' 4.4-2.9L5 6.9l5.1 1.6z"/>',
  // A cross in a square: the wounded mark of a dive.
  hurt: '<rect x="4" y="4" width="16" height="16" rx="3.5"/><path d="M12 8v8M8 12h8"/>',
  // A crate: the cargo mark of a loss.
  cargo: '<rect x="4" y="7" width="16" height="12" rx="1.5"/><path d="M4 11h16M10 7v4M14 7v4"/>',
  // A medal on its ribbon: the Honour mark of the season report.
  honour: '<circle cx="12" cy="9.5" r="5.5"/><path d="M8.8 14l-1.8 6.5 5-2.5 5 2.5-1.8-6.5"/>',
  // A pin: the place line of an open message.
  pin: '<path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z"/><circle cx="12" cy="10" r="2.2"/>',
} as const;

/** The name of one INBOX icon. */
export type InboxIcon = keyof typeof PATHS;

/** One icon at `size` px, hidden from a screen reader. The class
 *  `ib-ic-<name>` names it, so a test reads it without the paths, and
 *  `hollow` draws the faint outline of an absent mark (build plan §1). */
export function inboxIcon(name: InboxIcon, size = 14, hollow = false): string {
  return lineIcon(`ib-ic ib-ic-${name}${hollow ? ' ib-hollow' : ''}`, size, PATHS[name]);
}
